import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import { checkAdminSession, completeAdminSessionTransfer, adminLogout } from "../../utils/session";
import AdminService from "./AdminService";
const AdminAuthContext = createContext(null);
import { useNavigate } from "react-router";

export const AdminAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [canManageCustomers, setCanManageCustomers] = useState(false);
    const [checkingManageAccess, setCheckingManageAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    /**
     * Populate auth state from a /api/me user object.
     * Only activates for admin sessions (isAdmin === true).
     */
    const setAuthFromUser = useCallback((user) => {
        if (!user) return false;
        const roles = user.roles || [];
        setUserInfo(user);
        setUserRoles(roles);
        setIsAuthenticated(true);
        return true;
    }, []);

    /**
     * Check Manage Customers Access
     */
    const checkManageCustomersAccess = async () => {
        try {
            setCheckingManageAccess(true);
            const res = await AdminService.checkManageCustomers();
            const isAuthorized = res?.isAuthorized === true;
            setCanManageCustomers(isAuthorized);
        } catch (err) {
            console.error("ManageCustomers check failed:", err);
            setCanManageCustomers(false);
        } finally {
            setCheckingManageAccess(false);
        }
    };

    /**
     * On mount — check if we already have a valid admin session cookie.
     */
    const initSession = useCallback(async () => {
        try {
            const { authenticated, user } = await checkAdminSession();
            if (authenticated && user?.isAdmin) {
                setAuthFromUser(user);
                await checkManageCustomersAccess();
            }
        } catch (err) {
            console.error("Admin session check error:", err);
        } finally {
            setLoading(false);
        }
    }, [setAuthFromUser]);

    const login = () => {
        const w = 480,
            h = 640;
        const left = Math.round((screen.width - w) / 2);
        const top = Math.round((screen.height - h) / 2);

        window.open(
            "/login?target=admin",
            "unaric_admin_auth",
            `popup=yes,width=${w},height=${h},left=${left},top=${top}`,
        );
    };

    const logout = async () => {
        await adminLogout();
        setUserInfo(null);
        setUserRoles([]);
        setIsAuthenticated(false);
        setCanManageCustomers(false);
        navigate("/");
        window.location.reload();
    };

    /**
     * Role Check Helpers
     */
    const hasRole = (role) => userRoles.includes(role);

    const hasAnyRole = (roles = []) =>
        roles.some((role) => userRoles.includes(role));

    /**
     * Listen for popup auth — session-based, only a transferToken is received.
     */
    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.origin !== window.location.origin) return;

            const data = event.data || {};

            if (data.type === "admin-auth-success" && data.transferToken) {
                try {
                    const { authenticated, user } = await completeAdminSessionTransfer(data.transferToken);

                    if (authenticated && user) {
                        setAuthFromUser(user);

                        // Call onboarding API after successful login
                        if (user.tenantId) {
                            await AdminService.postAdminOnboarding();
                        }
                        await checkManageCustomersAccess();
                    }
                } catch (err) {
                    console.error("Admin auth transfer failed:", err);
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [setAuthFromUser]);

    useEffect(() => {
        initSession();
    }, [initSession]);

    return (
        <AdminAuthContext.Provider
            value={{
                isAuthenticated,
                userInfo,
                userRoles,
                login,
                logout,
                hasRole,
                hasAnyRole,
                canManageCustomers,
                checkingManageAccess,
                loading,
            }}
        >
            {children}
        </AdminAuthContext.Provider>
    );
};

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error("useAdminAuth must be used inside AdminAuthProvider");
    }
    return context;
};
